import axios from "axios";
import { getCloudFunctionUrl } from "../../../utils/cloudFunctions";
import {
  MICA_CONNECT_PLATFORM_META,
  registerMicaConnectDriver,
} from "./registry";

const waitForFacebookSdk = (timeoutMs = 15000) =>
  new Promise((resolve, reject) => {
    const started = Date.now();
    const tick = () => {
      if (window.FB?.login) {
        resolve(window.FB);
        return;
      }
      if (Date.now() - started > timeoutMs) {
        reject(new Error("Facebook SDK unavailable"));
        return;
      }
      window.setTimeout(tick, 200);
    };
    tick();
  });

/**
 * Meta driver: reuses FB.login + saveTokenFacebook (no duplicated OAuth exchange).
 * Instagram Business is validated server-side after Meta token save.
 *
 * validate() runs on page load; connect() must be called from a user tap so
 * FB.login opens under a valid iOS gesture (no awaits before FB.login).
 */
const metaDriver = {
  id: MICA_CONNECT_PLATFORM_META,
  label: "Meta",
  description:
    "Conecta Meta Business. Se requiere una cuenta de Instagram Business enlazada a una Página.",

  async validate({ token }) {
    if (!token) {
      return { ok: false, message: "Missing connect token" };
    }

    const { data: validated } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl(
        "validateMicaConnectToken"
      )}/validateMicaConnectToken`,
      data: { token },
    });

    if (!validated?.ok) {
      return {
        ok: false,
        message: validated?.message || "Token inválido o expirado",
        code: validated?.code,
      };
    }

    // Warm SDK so connect() can call FB.login synchronously on tap.
    await waitForFacebookSdk();

    return { ok: true };
  },

  async connect({ token, onStatus }) {
    if (!token) {
      return { ok: false, message: "Missing connect token" };
    }

    const FB = window.FB;
    if (!FB?.login) {
      return {
        ok: false,
        message: "Facebook SDK unavailable",
        code: "sdk_unavailable",
      };
    }

    // FB.login must run before any await — iOS blocks popups otherwise.
    onStatus?.("Abriendo Meta…");
    const authResponse = await new Promise((resolve) => {
      FB.login(
        (response) => {
          resolve(response || {});
        },
        {
          config_id: import.meta.env.VITE_FACEBOOK_CONFIG_ID,
        }
      );
    });

    if (authResponse.status === "unknown") {
      return { ok: false, message: "Conexión cancelada", code: "cancelled" };
    }
    if (authResponse.status !== "connected" || !authResponse.authResponse) {
      return {
        ok: false,
        message: "No se pudo conectar con Meta",
        code: "meta_login_failed",
      };
    }

    const { accessToken, userID } = authResponse.authResponse;
    onStatus?.("Guardando conexión…");

    // No Ads Authorization header — micaConnectToken authorizes the write.
    const { data: saved } = await axios({
      method: "post",
      url: `${getCloudFunctionUrl("saveTokenFacebook")}/saveTokenFacebook`,
      data: {
        id: userID,
        accessToken,
        name: "",
        micaConnectToken: token,
      },
    });

    if (!saved?.ok) {
      return {
        ok: false,
        message: saved?.message || "No se pudo guardar la conexión de Meta",
        code: saved?.code,
      };
    }

    return {
      ok: true,
      data: {
        platform: MICA_CONNECT_PLATFORM_META,
      },
    };
  },
};

registerMicaConnectDriver(MICA_CONNECT_PLATFORM_META, metaDriver);

export default metaDriver;
